import React from "react";
import { Box, Typography, MenuProps } from "@mui/material";
import clsx from "clsx";

import { ExerciseTagDto } from "@growth-machine-llc/stridist-api-client";
import { AddedLozenge } from "../../item/AddedLozenge";
import TagChip from "./TagChip";
import { ExerciseTagMultiSelect } from "../ExerciseTagMultiSelect";
import { exerciseLibraryStyles } from "../exerciseLibraryStyles";

interface ExerciseTagSelectProps {
  label: string;
  onChange?: (options: ExerciseTagDto[]) => void;
  selectedOptions: ExerciseTagDto[];
  classes?: Partial<Record<"label" | "container", string>>;
  menuProps?: Partial<MenuProps>;
  disabled?: boolean;
}

const ExerciseTagSelect = ({
  label,
  onChange,
  selectedOptions,
  classes,
  disabled,
}: ExerciseTagSelectProps) => {
  const s = exerciseLibraryStyles();

  const lozengeRef = React.useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);

  const handleLozengeClick = () => {
    if (disabled) return;
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  return (
    <Box className={classes?.container}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        mt={2}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          mb={1}
        >
          <Typography
            variant="h6"
            sx={{ m: 0 }}
            className={clsx(s.editorLabel, classes?.label)}
          >
            {label}
          </Typography>
        </Box>
        {!disabled && (
          <Box>
            <ExerciseTagMultiSelect
              onClose={handleMenuClose}
              onChangeSelectedOptions={(selected) => {
                if (onChange) {
                  onChange(selected);
                }
              }}
              initialSelected={selectedOptions}
              open={menuOpen}
              setOpen={setMenuOpen}
            />
          </Box>
        )}
      </Box>

      <Box gap={1} display="flex" flexWrap="wrap">
        {(!selectedOptions || selectedOptions.length === 0) &&
          (disabled ? (
            <Typography variant="body2" children="No tags selected" />
          ) : (
            <Box ref={lozengeRef}>
              <AddedLozenge
                sx={{
                  "&.MuiBox-root": { m: 0 },
                  width: "fit-content",
                }}
                className={s.defaultOption}
                text={`Start by selecting a tag`}
                onClick={handleLozengeClick}
              />
            </Box>
          ))}

        {selectedOptions &&
          selectedOptions.map((tag) => (
            <TagChip
              key={String(tag.id)}
              primaryColor={tag.primaryColor}
              text={tag.title}
              onRemoveClick={() => {
                if (onChange) {
                  onChange(selectedOptions.filter((t) => t.id !== tag.id));
                }
              }}
              disabled={disabled}
            />
          ))}
      </Box>
    </Box>
  );
};

export default ExerciseTagSelect;
